import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Seconds after which the "analyzing" stage hands over to "detecting chapters". */
const DETECTING_STAGE_START_SECONDS = 45;
/** How long each rotating value-proposition message is shown. */
const VALUE_PROP_ROTATION_SECONDS = 6;

/**
 * Engaging wait state for the chapter extraction call: a multi-stage progress
 * indicator with rotating value-proposition messages and an honest time
 * expectation. Purely time-driven (the endpoint reports no real progress).
 */
export default function ExtractionProgress() {
	const { t } = useTranslation("common");
	const [elapsedSeconds, setElapsedSeconds] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setElapsedSeconds((seconds) => seconds + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, []);

	// Ease towards 95% over ~2 minutes: the bar keeps moving the whole time
	// but never claims to be finished before the request resolves.
	const progressValue = Math.min(
		95,
		Math.round(100 * (1 - Math.exp(-elapsedSeconds / 55))),
	);

	const isDetecting = elapsedSeconds >= DETECTING_STAGE_START_SECONDS;
	const stages: Array<{
		key: string;
		label: string;
		state: "done" | "active" | "pending";
	}> = [
		{
			key: "uploaded",
			label: t("finishSignup.extracting.stageUploaded"),
			state: "done",
		},
		{
			key: "analyzing",
			label: t("finishSignup.extracting.stageAnalyzing"),
			state: isDetecting ? "done" : "active",
		},
		{
			key: "detecting",
			label: t("finishSignup.extracting.stageDetecting"),
			state: isDetecting ? "active" : "pending",
		},
	];

	const valueProps = [
		t("finishSignup.extracting.valueProp1"),
		t("finishSignup.extracting.valueProp2"),
		t("finishSignup.extracting.valueProp3"),
		t("finishSignup.extracting.valueProp4"),
	];
	const currentValueProp =
		valueProps[
			Math.floor(elapsedSeconds / VALUE_PROP_ROTATION_SECONDS) %
				valueProps.length
		];

	return (
		<div className="w-full space-y-4 rounded-lg border border-primary/20 bg-background/50 p-4">
			<Progress value={progressValue} />

			<ul className="space-y-2">
				{stages.map((stage) => (
					<li key={stage.key} className="flex items-center gap-2 text-sm">
						{stage.state === "done" ? (
							<CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
						) : stage.state === "active" ? (
							<Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
						) : (
							<Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
						)}
						<span
							className={
								stage.state === "active"
									? "font-medium"
									: "text-muted-foreground"
							}
						>
							{stage.label}
						</span>
					</li>
				))}
			</ul>

			<p
				key={currentValueProp}
				className="min-h-10 text-sm text-muted-foreground animate-in fade-in duration-700"
			>
				{currentValueProp}
			</p>

			<p className="text-xs text-muted-foreground/80">
				{t("finishSignup.extracting.eta")}
			</p>
		</div>
	);
}
